/**
 * PaymentMethodSetup Component
 * 
 * Embedded Stripe payment form for adding/updating payment methods
 */

import React, { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Button } from '../ui/button';
import { AlertCircle } from 'lucide-react';

// Load Stripe.js (use your publishable key)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_YOUR_KEY');

/**
 * Payment Form Component (inside Elements provider)
 */
function PaymentForm({ locationId, onSuccess, onCancel, getAccessTokenSilently }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) return;
    
    setLoading(true);
    setError(null);

    try {
      // Submit payment element to Stripe
      const { error: submitError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.origin + '/billing',
        },
      });

      if (submitError) {
        setError(submitError.message);
        setLoading(false);
        return;
      }

      // Confirm with backend - USE LOCATION-BASED ENDPOINT
      const token = await getAccessTokenSilently();
      const apiUrl = process.env.REACT_APP_API_URL;
      
      const response = await fetch(
        `${apiUrl}/api/billing/confirm-payment/location/${locationId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            setup_intent_id: setupIntent.id
          })
        }
      );

      const result = await response.json();
      
      if (result.success) {
        onSuccess();
      } else {
        setError('Unable to save payment method. Please try again.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('Unable to save payment method. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-gray-900/50 rounded-lg p-4">
        <PaymentElement 
          options={{
            layout: 'tabs',
            defaultValues: {
              billingDetails: {
                email: '',
              }
            }
          }}
        />
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || loading}
          className="flex-1 bg-gradient-to-r from-[#3AD0E6] to-[#3630A4] text-white"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            'Save Payment Method'
          )}
        </Button>
      </div>
    </form>
  );
}

/**
 * Main PaymentMethodSetup Component
 */
export default function PaymentMethodSetup({ 
  locationId, 
  clientSecret, 
  onSuccess, 
  onCancel,
  getAccessTokenSilently 
}) {
  if (!clientSecret) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AD0E6] mx-auto mb-4"></div>
        <p className="text-gray-400">Loading payment form...</p>
      </div>
    );
  }

  const appearance = {
    theme: 'night',
    variables: {
      colorPrimary: '#3AD0E6',
      colorBackground: '#1f2937',
      colorText: '#ffffff',
      colorDanger: '#ef4444',
      fontFamily: 'system-ui, sans-serif',
      borderRadius: '8px',
    },
  };

  return (
    <Elements 
      stripe={stripePromise} 
      options={{ 
        clientSecret,
        appearance 
      }}
    >
      <PaymentForm 
        locationId={locationId}
        onSuccess={onSuccess}
        onCancel={onCancel}
        getAccessTokenSilently={getAccessTokenSilently}
      />
    </Elements>
  );
}

